import { Button, Checkbox, ListItemText, MenuItem, Select, Tooltip } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DatePicker, message } from "antd";
import dayjs from "dayjs";
import moment from "moment";

import HeaderBread from "../../../../../components/HeaderBread";
import { useDataLayerValue } from "../../../../../DataLayer/DataLayer";
import Layout from "../../../../../Layout";
import CustomHeaderTitle from "../../../../../components/constants/CustomHeaderTitle";
import CustomizedTable from "../../../../../components/UI/CustomizedTable";
import Logs from "../../../../../components/Logs";
import ReportDownloads from "../../../../../components/ReportDownloads";
import SearchField from "../../../../../components/SearchField";
import SwitchBar from "../../../../../components/Pages/SwitchBar";
import {
  getEntertainmentSalesReport,
  updateEntertainmentTransactionStatus,
  getEntertainmentRvcs,
  getEntertainmentMenuItems,
} from "../../../../../utils/ApiCalls/BackOffice/Entertainment";
import { createReportDownload } from "../../../../../utils/ApiCalls/ReportDownloads";
import { addlog } from "../../../../../utils/ApiCalls/Logs";
import { LOG_ACTIONS, LOG_TYPES } from "../../../../../utils/Constant/LogConstant";
import { getReportFileName } from "../../../../../utils/Constant/ReportDownloadLabels";
import { useDebounce } from "../../../../../hooks";
import { checkPermission } from "../../../../../utils/Validation/checkPermission";

const { RangePicker } = DatePicker;
const PAGE_PATH = "/shore/back-office/entertainment/report/sales";

const getDefaultDateRange = () =>
  JSON.stringify([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

const parseDateRange = (range) => {
  if (!range) return null;
  try {
    const [start, end] = JSON.parse(range);
    if (start && end) return [dayjs(start, "YYYY-MM-DD"), dayjs(end, "YYYY-MM-DD")];
  } catch (e) {
    // unparsable - treat as no selection
  }
  return null;
};

const statusChipStyle = (status) => {
  const base = {
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
  };
  switch ((status || "").toLowerCase()) {
    case "confirmed":
      return { ...base, background: "#0ab402" };
    case "claimed":
      return { ...base, background: "#1565c0" };
    case "voided":
      return { ...base, background: "#616161" };
    case "failed":
      return { ...base, background: "#eb6363" };
    case "pending":
      return { ...base, background: "#f5a623" };
    default:
      return { ...base, background: "#9e9e9e" };
  }
};

const formatAmount = (val) => (val != null ? Number(val).toFixed(2) : "-");

const EntertainmentSalesReport = () => {
  const locationP = useLocation();
  const navigate = useNavigate();
  const [{ permission, userDetails }, dispatch] = useDataLayerValue();
  const [topTab, setTopTab] = useState("report");

  const navigation = [
    { title: "Home", route: "/" },
    { title: "Report", route: "/shore/back-office/entertainment/report" },
    { title: "Sales Report", route: PAGE_PATH },
  ];

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [dateRange, setDateRange] = useState(() => {
    const params = new URLSearchParams(locationP.search);
    return params.has("dateRange") ? params.get("dateRange") : getDefaultDateRange();
  });
  const parseIdList = (val) => (val ? val.split(",").filter(Boolean) : []);
  const [statuses, setStatuses] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("status"))
  );
  const [page, setPage] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("page")) || 1
  );
  const [pageSize, setPageSize] = useState(
    () => parseInt(new URLSearchParams(locationP.search).get("pageSize")) || 10
  );
  const [search, setSearch] = useState(
    () => new URLSearchParams(locationP.search).get("search") || ""
  );
  const debouncedSearch = useDebounce(search, 1000);
  const [rvcIds, setRvcIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("rvcIds"))
  );
  const [itemIds, setItemIds] = useState(
    () => parseIdList(new URLSearchParams(locationP.search).get("itemIds"))
  );
  const [rvcOptions, setRvcOptions] = useState([]);
  const [itemOptions, setItemOptions] = useState([]);

  useEffect(() => {
    (async () => {
      const [rvcRes, itemRes] = await Promise.all([
        getEntertainmentRvcs({ limit: 1000 }),
        getEntertainmentMenuItems({ limit: 1000 }),
      ]);
      const sortAlphaNumeric = (a, b) =>
        a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" });
      setRvcOptions(
        (rvcRes?.data || [])
          .slice()
          .sort((a, b) => sortAlphaNumeric(`${a.rvcId}-${a.name}`, `${b.rvcId}-${b.name}`))
      );
      setItemOptions(
        (itemRes?.data || []).slice().sort((a, b) => sortAlphaNumeric(a.name, b.name))
      );
    })();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("page", page);
    params.set("pageSize", pageSize);
    if (search) params.set("search", search);
    if (statuses.length) params.set("status", statuses.join(","));
    if (rvcIds.length) params.set("rvcIds", rvcIds.join(","));
    if (itemIds.length) params.set("itemIds", itemIds.join(","));
    params.set("dateRange", dateRange);
    navigate({ search: params.toString() }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, rvcIds, itemIds]);

  const columns = [
    {
      title: "Sl",
      key: "sl",
      width: 40,
      render: (val, rec, idx) => (page - 1) * pageSize + idx + 1,
    },
    { title: "Fidelio Business Date", dataIndex: "fidelioBusinessDate", key: "fidelioBusinessDate", width: 150 },
    { title: "Transaction ID", dataIndex: "transactionId", key: "transactionId", width: 180 },
    { title: "Cabin Number", dataIndex: "cabinNumber", key: "cabinNumber", width: 120 },
    { title: "Guest Name", dataIndex: "guestName", key: "guestName", width: 160 },
    { title: "Acc ID", dataIndex: "accId", key: "accId", width: 100 },
    { title: "RVC Number", dataIndex: "rvcNumber", key: "rvcNumber", width: 110 },
    { title: "RVC Name", dataIndex: "rvcName", key: "rvcName", width: 150, render: (val) => val?.english },
    { title: "Item Number", dataIndex: "itemNumber", key: "itemNumber", width: 110 },
    { title: "Item Name", dataIndex: "itemName", key: "itemName", width: 160, render: (val) => val?.english },
    {
      title: "Unit Price",
      dataIndex: "unitPrice",
      key: "unitPrice",
      width: 100,
      render: (val) => formatAmount(val),
    },
    { title: "Quantity", dataIndex: "quantity", key: "quantity", width: 90 },
    {
      title: "Total Price",
      dataIndex: "totalPrice",
      key: "totalPrice",
      width: 110,
      render: (val) => formatAmount(val),
    },
    {
      title: "Transaction At",
      dataIndex: "transactionAt",
      key: "transactionAt",
      width: 150,
      render: (val) => (val ? moment(val).format("DD-MM-YYYY HH:mm") : "-"),
    },
    {
      title: "Status",
      dataIndex: "transactionStatus",
      key: "transactionStatus",
      width: 110,
      render: (val, row) => {
        const displayStatus = row.voided ? "Voided" : val;
        return row.voided && row.voidReason ? (
          <Tooltip title={row.voidReason} enterDelay={0} enterTouchDelay={0} arrow>
            <span style={statusChipStyle(displayStatus)}>{displayStatus}</span>
          </Tooltip>
        ) : (
          <span style={statusChipStyle(displayStatus)}>{displayStatus || "-"}</span>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 140,
      render: (val, row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {row.transactionStatus === "Confirmed" && !row.voided && (
            <Button
              variant="contained"
              size="small"
              style={{ background: "#1565c0", fontWeight: 500 }}
              onClick={() => handleClaim(row)}
            >
              Claim
            </Button>
          )}
          <button
            className="btn delete_btn"
            title="View"
            onClick={() => navigate(`/shore/back-office/entertainment/report/view/${row.id}`)}
          >
            <i className="ri-eye-line"></i>
          </button>
        </div>
      ),
    },
  ];

  const handleClaim = async (row) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const res = await updateEntertainmentTransactionStatus(row.id, "Claimed");
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.CLAIMED,
        LOG_TYPES.ENTERTAINMENT_TRANSACTION,
        {
          title: `Transaction ${row.transactionId}`,
          transactionId: row.transactionId,
          cabinNumber: row.cabinNumber,
          guestName: row.guestName,
          amount: row.totalPrice,
        },
        userDetails.id
      );
      await getData(page);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const buildFilterArgs = () => {
    const args = {};
    args.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (debouncedSearch) args.search = debouncedSearch;
    if (statuses.length) args.status = statuses.join(",");
    if (rvcIds.length) args.rvcIds = rvcIds.join(",");
    if (itemIds.length) args.itemIds = itemIds.join(",");
    try {
      const [fromDate, toDate] = JSON.parse(dateRange || "[]");
      if (fromDate && toDate) {
        args.fromDate = fromDate;
        args.toDate = toDate;
      }
    } catch (e) {
      // no date range selected
    }
    return args;
  };

  const getData = async (value) => {
    dispatch({ type: "SET_LOADING", loading: true });
    const args = { page: value || page, limit: pageSize, ...buildFilterArgs() };
    let data = await getEntertainmentSalesReport(args);
    if (data.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: data.error, type: "error" } });
    } else {
      setRows(data.data || []);
      setCount(data.totalCount || 0);
    }
    dispatch({ type: "SET_LOADING", loading: false });
  };

  const handleRequestDownload = async () => {
    if (!checkPermission(permission, "download_per", "Sales Reports", window?.location?.pathname)) {
      message.error("Download permission missing");
      return;
    }
    const res = await createReportDownload({
      moduleName: "entertainment_sales_report",
      createdBy: userDetails.id,
      searchParams: buildFilterArgs(),
    });
    if (res?.error) {
      dispatch({ type: "SET_ERROR_MESSAGE", errMess: { message: res.error, type: "error" } });
    } else {
      addlog(
        LOG_ACTIONS.REQUESTED,
        LOG_TYPES.REPORT_DOWNLOADS,
        {
          title: "Sales Report download requested",
          moduleName: "entertainment_sales_report",
          reportDownloadId: res?.id,
          fileName: getReportFileName("entertainment_sales_report", res?.createdAt, res?.id, res?.timezone),
        },
        userDetails.id
      );
      dispatch({
        type: "SET_ERROR_MESSAGE",
        errMess: { message: "Download requested - check Download Logs tab", type: "success" },
      });
    }
  };

  useEffect(() => {
    getData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, debouncedSearch, statuses, dateRange, rvcIds, itemIds]);

  return (
    <Layout type={"read_per"} module_data={"Sales Report"}>
      <CustomHeaderTitle title={"Sales Report"} />
      <div>
        <HeaderBread navigation={navigation} title={"Sales Report"} />
      </div>
      <section className="content" style={{ marginTop: "20px" }}>
        <div className="container-fluid">
          <SwitchBar
            tabs={[
              { title: "Sales Report", value: "report" },
              { title: "History Logs", value: "logs" },
              { title: "Download Logs", value: "downloads" },
            ]}
            activeTab={topTab}
            setActiveTab={setTopTab}
          />

          {topTab === "downloads" ? (
            <ReportDownloads module="entertainment_sales_report" />
          ) : topTab !== "report" ? (
            <Logs
              type={LOG_TYPES.ENTERTAINMENT_TRANSACTION}
              actionFilter={LOG_ACTIONS.CLAIMED}
              extendedColors
            />
          ) : (
          <div className="card">
            <div className="card-header two_item" style={{ alignItems: "center" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
                <Select
                  multiple
                  style={{ width: "200px", height: "40px" }}
                  displayEmpty
                  value={statuses}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setStatuses(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) => (selected.length ? selected.join(", ") : "All Status")}
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={statuses.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {["Pending", "Confirmed", "Claimed", "Voided", "Failed"].map((statusOption) => (
                    <MenuItem key={statusOption} value={statusOption}>
                      <Checkbox checked={statuses.includes(statusOption)} />
                      <ListItemText primary={statusOption} />
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={rvcIds}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setRvcIds(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? rvcOptions
                          .filter((rvc) => selected.includes(String(rvc.id)))
                          .map((rvc) => `${rvc.rvcId}-${rvc.name}`)
                          .join(", ")
                      : "Filter by Revenue Center"
                  }
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={rvcIds.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {rvcOptions.map((rvc) => (
                    <MenuItem key={rvc.id} value={String(rvc.id)}>
                      <Checkbox checked={rvcIds.includes(String(rvc.id))} />
                      <ListItemText primary={`${rvc.rvcId}-${rvc.name}`} />
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  multiple
                  style={{ width: "220px", height: "40px" }}
                  displayEmpty
                  value={itemIds}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setItemIds(newValue.includes("__ALL__") ? [] : newValue);
                    setPage(1);
                  }}
                  renderValue={(selected) =>
                    selected.length
                      ? itemOptions
                          .filter((item) => selected.includes(String(item.id)))
                          .map((item) => item.name)
                          .join(", ")
                      : "Filter by Item Name"
                  }
                >
                  <MenuItem value="__ALL__">
                    <Checkbox checked={itemIds.length === 0} />
                    <ListItemText primary="All" />
                  </MenuItem>
                  {itemOptions.map((item) => (
                    <MenuItem key={item.id} value={String(item.id)}>
                      <Checkbox checked={itemIds.includes(String(item.id))} />
                      <ListItemText primary={item.name} />
                    </MenuItem>
                  ))}
                </Select>
                <RangePicker
                  style={{ width: "250px" }}
                  onChange={(date, dateString) => {
                    setDateRange(dateString[0] && dateString[1] ? JSON.stringify(dateString) : "");
                    setPage(1);
                  }}
                  value={parseDateRange(dateRange)}
                  format={"YYYY-MM-DD"}
                />
                <Button
                  variant="contained"
                  style={{ background: "var(--red)", fontWeight: "500" }}
                  onClick={() => {
                    setStatuses([]);
                    setDateRange(getDefaultDateRange());
                    setSearch("");
                    setRvcIds([]);
                    setItemIds([]);
                    setPage(1);
                  }}
                >
                  Reset Filter
                </Button>
                <Button
                  variant="contained"
                  style={{ background: "#1565c0", fontWeight: "500" }}
                  onClick={handleRequestDownload}
                >
                  Request Download
                </Button>
              </div>
              <SearchField
                value={search}
                SearchValue={() => setPage(1)}
                setValue={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="card-body">
              <div className="table_holder">
                <CustomizedTable
                  setRows={setRows}
                  setPageSize={setPageSize}
                  setPage={setPage}
                  pageSize={pageSize}
                  page={page}
                  count={count}
                  columns={columns}
                  rows={rows}
                  title={"Sales Report"}
                />
              </div>
            </div>
          </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default EntertainmentSalesReport;
